import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { useFetchMessageTemplates } from "../hooks/useGetMessageTemplates";
import { classNames } from "../../../utils/helpers";

const MessageTemplates = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const formik = useFormikContext();
  const { data: templates, isLoading } = useFetchMessageTemplates();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const templateId = formik.values?.uuid_messages_templates;
    if (templateId && templateId !== selectedTemplate) {
      setSelectedTemplate(templateId);
    }
  }, [formik.values?.uuid_messages_templates]);

  useEffect(() => {
    let templateData = {};

    if (!templates.length) return;

    [templateData = {}] = templates.filter(
      (x) => x.uuid_messages_templates === selectedTemplate,
    );

    /* eslint-disable no-unused-vars */
    const filteredNullableData = Object.fromEntries(
      Object.entries(templateData).filter(([key, value]) => {
        if (value === null) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      }),
    );

    delete filteredNullableData.name;

    formik.setValues({
      ...formik.values,
      ...filteredNullableData,
    });
  }, [templates, selectedTemplate]);



  const options = useCallback(() => {
    if (!templates.length) return [];

    let filtered = templates.map((item) => ({
      value: item.uuid_messages_templates,
      label: item.name || "Sin nombre",
      preview: item.preview || item.content?.substring(0, 60) || "",
    }));

    if (searchTerm) {
      filtered = filtered.filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          opt.preview.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return filtered;
  }, [templates, searchTerm]);

  const filteredOptions = options();

  const handleSelect = useCallback(
    (templateId) => {
      const isSelected = selectedTemplate === templateId;

      if (isSelected) {
        setSelectedTemplate(null);
        formik.setFieldValue("uuid_messages_templates", null);
        return;
      }

      setSelectedTemplate(templateId);
      formik.setFieldValue("uuid_messages_templates", templateId);
    },
    [formik.setFieldValue, selectedTemplate],
  );

  const getDisplayName = useCallback(() => {
    if (!selectedTemplate) return "Plantillas Mensajes";
    const found = templates?.find(
      (t) => t.uuid_messages_templates === selectedTemplate,
    );
    return found?.name || "Plantillas Mensajes";
  }, [selectedTemplate, templates]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block w-max-[600px]" ref={dropdownRef}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={classNames(
          `
          inline-flex items-center justify-between w-max-[200px] px-3 py-2 
          text-sm font-medium text-gray-700 
          bg-white border border-gray-300 rounded-lg
          hover:bg-gray-50 hover:border-gray-400
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          transition-all duration-200
        `,
          {
            "ring-2 ring-blue-500 ring-offset-2 border-blue-500": isOpen,
            "opacity-50 cursor-not-allowed": isLoading,
          },
        )}
      >
        <span className="flex items-center gap-2">
          {isLoading ? (
            <span className="material-symbols-outlined text-gray-400 animate-spin">
              progress_activity
            </span>
          ) : (
            <span className="material-symbols-outlined text-gray-400 text-[20px]">
              description
            </span>
          )}
          <span className="truncate">
            {isLoading ? "Cargando..." : getDisplayName()}
          </span>
        </span>
        <span
          className={classNames(
            `material-symbols-outlined text-gray-400 text-[20px] transition-transform duration-200`,
            {
              "rotate-180": isOpen,
            },
          )}
        >
          expand_more
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2">
            {/* Buscador */}
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar plantilla..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4.5 py-2 pl-9 text-sm bg-gray-50 border border-gray-200 rounded-md 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  placeholder:text-gray-400 transition-all duration-200"
              />
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">
                search
              </span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute flex items-center justify-center right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 border-none bg-transparent focus:outline-none transition-colors duration-200"
                >
                  <span className="material-symbols-outlined text-[20px]">
                    close
                  </span>
                </button>
              )}
            </div>
            {/* Lista de plantillas */}
            {filteredOptions.length > 0 ? (
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                {filteredOptions.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={classNames(
                      `
                      group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer
                      transition-all duration-150
                      
                    `,
                      {
                        "bg-blue-50 text-blue-700":
                          selectedTemplate === opt.value,
                        "hover:bg-gray-50 text-gray-700":
                          selectedTemplate !== opt.value,
                      },
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {opt.label}
                        </span>
                      </div>
                      {opt.preview && (
                        <p className="text-xs text-gray-500 truncate">
                          {opt.preview}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-2 text-center py-4">
                <span className="material-symbols-outlined text-gray-300 text-[40px] block mx-auto mb-2">
                  description
                </span>
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? "No se encontraron plantillas"
                    : "No hay plantillas disponibles"}
                </p>
              </div>
            )}

            {/* Separador */}
            <div className="my-2 border-t border-gray-200"></div>

            {/* Botón Crear */}
            <button
              onClick={() => {
                window.open("/rcs/messagetemplates/create", "_blank");
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 
                text-sm font-medium text-blue-600 
                hover:bg-blue-50 rounded-md
                transition-colors duration-200 border-none focus:outline-none"
            >
              <span className="material-symbols-outlined text-[20px]">add</span>
              Crear nueva plantilla
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

MessageTemplates.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default MessageTemplates;
